import { ReactNode, forwardRef, useMemo } from 'react'
import {
  CrewPopover,
  CrewPopoverProps,
} from 'components/devextreme/crewPopover'
import { Popover } from 'devextreme-react'
import { SettingKeyType } from '@crew/enums/dist/app'
import { useUserSetting } from '@crew/states'
import { useTranslation } from '@crew/modules/i18n'

/**
 * resources.tsのlabel.tooltips配下のキー定義
 */
export const ToolTipTypes = {
  usageGuide: 'usageGuide',
  supportPortal: 'supportPortal',
  toggleRightSideBarButton: 'toggleRightSideBarButton',
  aiCreditRemaining: 'aiCreditRemaining',
  toggleAiAssistantButton: 'toggleAiAssistantButton',
  toggleLeftSideBarButton: 'toggleLeftSideBarButton',
  notification: 'notification',
  favorite: 'favorite',
  projectDetailFileDisplayModeList: 'projectDetailFileDisplayModeList',
  projectDetailFileDisplayModeGrid: 'projectDetailFileDisplayModeGrid',
  eventDisplayModeList: 'eventDisplayModeList',
  eventDisplayModeCalendar: 'eventDisplayModeCalendar',
  taskDisplayModeList: 'taskDisplayModeList',
  taskDisplayModeKanban: 'taskDisplayModeKanban',
  taskDisplayModeCalendar: 'taskDisplayModeCalendar',
  taskDisplayModeTimeline: 'taskDisplayModeTimeline',
  taskDisplayModeGantt: 'taskDisplayModeGantt',
  taskDisplayModeEvm: 'taskDisplayModeEvm',
  saveFilterButton: 'saveFilterButton',
  filterSettingsButton: 'filterSettingsButton',
  moveFilesButton: 'moveFilesButton',
  bulkDownloadButton: 'bulkDownloadButton',
  bulkAddTagsButton: 'bulkAddTagsButton',
  bulkDeleteFilesButton: 'bulkDeleteFilesButton',
  // 右上のヘルプボタン、これだけ2項目（ガイド, サポートポータル）表示するので特別
  helpButton: 'helpButton',
  myTaskDisplayModeList: 'myTaskDisplayModeList',
  myTaskDisplayModeCalendar: 'myTaskDisplayModeCalendar',
  myTaskDisplayModeGantt: 'myTaskDisplayModeGantt',
  globalMenuTaskDisplayModeList: 'globalMenuTaskDisplayModeList',
  globalMenuTaskDisplayModeCalendar: 'globalMenuTaskDisplayModeCalendar',
  globalMenuTaskDisplayModeGantt: 'globalMenuTaskDisplayModeGantt',
} as const

export type TooltipTypes = (typeof ToolTipTypes)[keyof typeof ToolTipTypes]

// type, children のいずれかで内容を指定する
// type -> 通常のツールチップ（翻訳キー指定）
// children -> 自由に内容を指定したい場合
export type CrewGuideToolTipProps = Omit<CrewPopoverProps, 'children'> &
  (
    | {
        /**
         * 翻訳キーのタイプ (resources.tsのlabel.tooltips配下のキーと1:1対応)
         *
         * label.tooltips.{type}.full と label.tooltips.{type}.short を参照:
         * - ガイドON: label.tooltips.{type}.full.title / description
         * - ガイドOFF: label.tooltips.{type}.short
         */
        type: TooltipTypes
        children?: never
      }
    | {
        type?: never
        children: ReactNode
      }
  )

export type GuideItem = {
  title: string
  description: string
}

export const CrewGuideToolTip = forwardRef<Popover, CrewGuideToolTipProps>(
  (
    {
      target,
      position = 'bottom',
      showEvent = { name: 'mouseenter', delay: 500 },
      hideEvent = 'mouseleave',
      maxWidth = '400',
      type,
      children,
      ...popoverProps
    },
    ref
  ) => {
    const { t } = useTranslation()
    const showStartUpGuide = useUserSetting(
      SettingKeyType.DisplayTutorial,
      'false'
    )
    const shouldShowDetail = showStartUpGuide === 'true'

    const content = useMemo(() => {
      // children, type の優先順位で判定
      if (children) {
        return children
      }

      if (type) {
        // ヘルプボタンは特殊な描画（複数項目表示）
        if (type === ToolTipTypes.helpButton) {
          if (shouldShowDetail) {
            return (
              <div className="flex flex-col gap-y-2 shrink-0">
                <div>
                  <div className="font-bold mb-1">
                    {t('label.tooltips.usageGuide.full.title')}
                  </div>
                  <div>{t('label.tooltips.usageGuide.full.description')}</div>
                </div>
                <div>
                  <div className="font-bold mb-1">
                    {t('label.tooltips.supportPortal.full.title')}
                  </div>
                  <div>
                    {t('label.tooltips.supportPortal.full.description')}
                  </div>
                </div>
              </div>
            )
          }
          return t('label.tooltips.helpButton.short')
        }

        // ツールチップ（ガイドあり/ なし）
        const mode = shouldShowDetail ? 'full' : 'short'
        const translationKey = `label.tooltips.${type}.${mode}`

        if (shouldShowDetail) {
          return (
            <div className="flex flex-col gap-y-1 shrink-0">
              {t(`${translationKey}.title`) !== '' && (
                <div className="font-bold">{t(`${translationKey}.title`)}</div>
              )}
              {t(`${translationKey}.description`) !== '' && (
                <div>{t(`${translationKey}.description`)}</div>
              )}
            </div>
          )
        }
        return t(translationKey)
      }

      return null
    }, [children, type, shouldShowDetail, t])

    return (
      <CrewPopover
        ref={ref}
        target={target}
        showEvent={showEvent}
        hideEvent={hideEvent}
        position={position}
        maxWidth={maxWidth}
        {...popoverProps}
      >
        <div className="text-sm crew-text-default">{content}</div>
      </CrewPopover>
    )
  }
)
